/**
 * Financeiro > Importação de Extrato (CSV).
 *
 * As correlações de classificação moram em tela própria (Financeiro >
 * Correlações de Importação): elas valem também para o upload da fatura de
 * cartão, não só para o extrato.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Link2, RefreshCw, FileText } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import { useUserPreferences, PREF_FIN_IMPORT } from '@hooks/useUserPreferences';
import type { Company } from '@/types/company';
import { formatCnpj } from '@/types/company';
import type { FinancialAccount, StatementImportResult } from '@/types/financial';
import { formatCurrencyBRL, formatDateBR } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  selectCls,
  labelCls,
  primaryBtnCls,
} from './financialShared';

interface TransactionImportPrefs extends Record<string, unknown> {
  company_id?: number;
  account_id?: number;
}

export const TransactionImportView: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [accountId, setAccountId] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<StatementImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<TransactionImportPrefs>(PREF_FIN_IMPORT);
  const accountRestored = useRef(false);

  // Empresas — espera as preferências para não selecionar a primeira empresa antes da restaurada
  useEffect(() => {
    if (!prefsLoaded) return;
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length === 0) return;
        const saved = list.find((c) => c.company_id === prefs?.company_id);
        setCompanyId(saved?.company_id ?? list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  // Persiste os filtros
  useEffect(() => {
    if (!prefsLoaded || companyId == null) return;
    savePrefs({ company_id: companyId, account_id: accountId ?? undefined });
  }, [prefsLoaded, companyId, accountId, savePrefs]);

  // Contas da empresa
  useEffect(() => {
    if (companyId == null) return;
    setAccounts([]);
    setAccountId(null);
    financialService
      .getAccounts(companyId)
      .then((list) => {
        setAccounts(list);
        if (list.length === 0) return;
        // Restaura a conta salva apenas na primeira carga; ao trocar de empresa vale a primeira conta dela.
        const saved = accountRestored.current
          ? undefined
          : list.find((a) => a.idAccountsMain === prefs?.account_id);
        accountRestored.current = true;
        setAccountId(saved?.idAccountsMain ?? list[0].idAccountsMain);
      })
      .catch(() => setAccounts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleImport = async () => {
    if (!selectedFile || companyId == null || accountId == null) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await financialService.importStatement(selectedFile, companyId, accountId);
      setImportResult(result);
      setNotice(`Importação concluída — ${result.insertedCount} lançamento(s) inserido(s)`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao importar extrato');
    } finally {
      setImporting(false);
    }
  };

  return (
    <PermissionGuard permission="financial.import.execute" fallback={<PermissionFallback label="a importação de extrato" />}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Importação de Extrato</h1>
            <p className="text-sm text-slate-500 mt-1">Importe extratos bancários (CSV) para a conta corrente</p>
          </div>
          <Link
            to="/financial/import-correlations"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
          >
            <Link2 size={15} /> Gerenciar correlações
          </Link>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} />}

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Conta</label>
              <select
                className={selectCls + ' w-64'}
                value={accountId ?? ''}
                onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
              >
                {accounts.length === 0 && <option value="">Nenhuma conta disponível</option>}
                {accounts.map((a) => (
                  <option key={a.idAccountsMain} value={a.idAccountsMain}>
                    {a.accountsDescription}
                    {a.bankName ? ` — ${a.bankName}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Arquivo (CSV)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="block text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 file:text-sm file:font-medium hover:file:bg-emerald-100"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <button
              className={primaryBtnCls}
              onClick={handleImport}
              disabled={importing || !selectedFile || accountId == null}
            >
              {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
              {importing ? 'Importando...' : 'Importar'}
            </button>
          </div>
        </div>

        {importing && <LoadingSpinner />}

        {importResult && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-sm text-slate-500">Lançamentos inseridos</p>
              <p className="text-2xl font-bold text-emerald-600">{importResult.insertedCount}</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <FileText size={16} className="text-amber-600" />
                <h3 className="text-sm font-semibold text-slate-700">
                  Ausentes no arquivo — conciliação ({importResult.missingFromFile.length})
                </h3>
              </div>
              {importResult.missingFromFile.length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-400">
                  Nenhum lançamento do sistema ausente no arquivo. Extrato conciliado.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.missingFromFile.map((row, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-2 px-3 whitespace-nowrap text-slate-700">{formatDateBR(row.date)}</td>
                          <td className="py-2 px-3 text-slate-700">{row.description}</td>
                          <td className={clsx('py-2 px-3 text-right whitespace-nowrap font-medium', row.amount < 0 ? 'text-red-600' : 'text-emerald-600')}>
                            {formatCurrencyBRL(row.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default TransactionImportView;
